// src/services/userService.ts
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { cloudFunctions, db } from './firebase';
import { logTechnicalError } from './analytics';
import { isPaidSubscriptionTier, isSubscriptionTier, SUBSCRIPTION_PLANS, SubscriptionTier } from '../config/subscriptionPlans';
import { getNextMonthlyPeriodEnd } from '../utils/subscriptionUsage';

const COLLECTION_NAME = 'users';

interface EnsureUserProfileResult {
  created: boolean;
  restoredUsage: boolean;
}

interface RecordCompletedMessageUsageResult {
  counted: boolean;
  reason: string;
}

const ensureUserProfileCallable = httpsCallable<unknown, EnsureUserProfileResult>(
  cloudFunctions,
  'ensureUserProfile'
);

const recordCompletedMessageUsageCallable = httpsCallable<
  { chatId: string; userMessageId: string; botMessageId: string },
  RecordCompletedMessageUsageResult
>(cloudFunctions, 'recordCompletedMessageUsage');

export interface UserProfile {
  email: string | null;
  tier: SubscriptionTier;
  usage: {
    messagesCount: number;
    periodEnd: number; // Timestamp en milisegundos
  };
  createdAt: any;
  onboardingCompleted?: boolean;
}

const normalizeEmail = (email: string | null | undefined): string | null => {
  if (!email) return null;
  const trimmed = email.trim();
  if (!trimmed) return null;
  return trimmed.toLowerCase();
};

export const ensureUserDoc = async (uid: string, email: string | null): Promise<void> => {
  try {
    const userRef = doc(db, COLLECTION_NAME, uid);
    const userSnap = await getDoc(userRef);
    const normalizedEmail = normalizeEmail(email);

    if (!userSnap.exists()) {
      await ensureUserProfileCallable({});
      return;
    }

    if (!normalizedEmail) return;
    const data = userSnap.data() as { email?: string | null };
    const existingEmail = normalizeEmail(data?.email ?? null);

    if (existingEmail !== normalizedEmail) {
      await updateDoc(userRef, { email: normalizedEmail });
    }
  } catch (error) {
    console.error('Error en ensureUserDoc:', error);
    logTechnicalError(error, 'userService:ensureUserDoc');
    throw error;
  }
};

/**
 * Obtiene el perfil del usuario.
 * Si no existe, lo crea automáticamente (Lazy Initialization).
 * Si el periodo ha caducado, resetea el contador (Lazy Reset).
 */
export const getOrCreateUserProfile = async (uid: string, email: string | null): Promise<UserProfile> => {
  try {
    const userRef = doc(db, COLLECTION_NAME, uid);
    const userSnap = await getDoc(userRef);
    const now = Date.now();

    // 1. Si el usuario no existe en la colección 'users', lo creamos (Plan Free por defecto)
    if (!userSnap.exists()) {
      await ensureUserProfileCallable({});
      const createdSnap = await getDoc(userRef);
      if (!createdSnap.exists()) {
        throw new Error('user_profile_creation_failed');
      }
      return createdSnap.data() as UserProfile;
    }

    const snapshotData = userSnap.data() as UserProfile;
    const data: UserProfile = {
      ...snapshotData,
      tier: isSubscriptionTier(snapshotData.tier) ? snapshotData.tier : 'free'
    };
    const createdAtDate = data.createdAt?.toDate ? data.createdAt.toDate() : new Date();

    // 2. Comprobar si el periodo ha expirado naturalmente
    const periodExpired = data.usage?.periodEnd ? now > data.usage.periodEnd : false;
    let nextPeriod = data.usage?.periodEnd || getNextMonthlyPeriodEnd(createdAtDate, new Date(now));
    let needReset = false;

    // Si ha expirado, calculamos el nuevo periodo basado en el día de anclaje
    if (periodExpired) {
      needReset = true;
      nextPeriod = getNextMonthlyPeriodEnd(createdAtDate, new Date(now));
    }

    // 3. Alinear fecha con Stripe para usuarios de pago
    if (isPaidSubscriptionTier(data.tier)) {
      try {
        const subscriptionsRef = collection(db, 'customers', uid, 'subscriptions');
        const subsSnapshot = await getDocs(subscriptionsRef);

        let latestStripePeriodEnd = 0;
        subsSnapshot.forEach((docSnap) => {
          const subData = docSnap.data();
          if (['active', 'trialing'].includes(subData.status)) {
            const docPeriodEnd = subData.current_period_end?.seconds * 1000 || 0;
            if (docPeriodEnd > latestStripePeriodEnd) {
              latestStripePeriodEnd = docPeriodEnd;
            }
          }
        });

        // Si hay una fecha válida en Stripe, esa manda sobre la calculada localmente
        if (latestStripePeriodEnd > 0) {
          nextPeriod = latestStripePeriodEnd;
        }
      } catch (e) {
        console.error("Error obteniendo suscripción de Stripe para reset:", e);
      }
    }

    // Aplicar cambios si hubo reset o si la fecha cambió (alineación con Stripe)
    const periodChanged = nextPeriod !== (data.usage?.periodEnd || 0);
    if (needReset || periodChanged) {
      const msgs = needReset ? 0 : (data.usage?.messagesCount || 0);

      return {
        ...data,
        usage: { messagesCount: msgs, periodEnd: nextPeriod }
      };
    }

    return data;
  } catch (error) {
    console.error("Error en getOrCreateUserProfile:", error);
    logTechnicalError(error, "userService:getOrCreateUserProfile");
    throw error;
  }
};

/**
 * Verifica si el usuario puede enviar mensajes según su plan.
 */
export const checkUsageLimit = async (uid: string, email: string | null): Promise<{ allowed: boolean; reason?: string }> => {
  try {
    // Obtenemos (o creamos) el perfil actualizado y con el ciclo correcto
    const userProfile = await getOrCreateUserProfile(uid, email);

    // Obtenemos los límites de su plan actual
    const planLimits = SUBSCRIPTION_PLANS[userProfile.tier];

    if (!planLimits) {
      // Si por error tiene un tier raro, bloqueamos por seguridad
      console.error('Tier desconocido:', userProfile.tier);
      return { allowed: false, reason: 'error_tier' };
    }

    // Comprobamos el límite
    if (userProfile.usage.messagesCount >= planLimits.maxMessages) {
      return { allowed: false, reason: 'limit_reached' };
    }

    return { allowed: true };
  } catch (error) {
    console.error('Error verificando límites:', error);
    logTechnicalError(error, "userService:checkUsageLimit");
    // En caso de error de red/db, por seguridad bloqueamos o permitimos según prefieras.
    // Para MVP: Bloqueamos para evitar costes descontrolados si la DB falla.
    return { allowed: false, reason: 'error_check' };
  }
};

export const recordCompletedMessageUsage = async (chatId: string, userMessageId: string, botMessageId: string) => {
  try {
    await recordCompletedMessageUsageCallable({ chatId, userMessageId, botMessageId });
  } catch (error) {
    console.error("Error contabilizando mensaje completado:", error);
    logTechnicalError(error, "userService:recordCompletedMessageUsage");
  }
};
